/**
 * The QueryClient (00-contracts §14.4).
 *
 * Three rules live here and nowhere else: what is worth retrying, who invalidates, and HVA SOM
 * SKJER NÅR EN MUTASJON FEILER. A mutation *declares* `meta.invalidates`; the single
 * `MutationCache` handler below does the invalidating, by prefix. That is the same "one place"
 * discipline as the audit log — a mutation that invalidates by hand is the second copy of a
 * cross-cutting concern.
 */
import { MutationCache, QueryCache, QueryClient, type QueryKey } from '@tanstack/react-query';

import { ApiError, errorKey, errorTitleKey, NetworkError } from '@/api/errors';
import { setActiveOrganizationId } from '@/auth/active-org';
import { emitToast, toastsShown } from '@/components/ui/toast-sink';
import { i18n } from '@/i18n';

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      invalidates?: readonly QueryKey[];
      /**
       * Sett denne når mutasjonen VISER feilen selv — et skjema som setter den ved feltet, en
       * dialog som blir stående med beskjeden i. Da er en toast i tillegg støy.
       *
       * Den er med vilje vrien å skrive og lett å se i en gjennomgang: standarden er at feil
       * SIES, og det å tie må begrunnes på stedet.
       */
      errorHandledLocally?: true;
    };
    queryMeta: { invalidates?: readonly QueryKey[] };
  }
}

const MAX_RETRIES = 2;

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;
  // A network blip is worth another go; a 4xx is a decision the server already made.
  if (error instanceof NetworkError) return true;
  if (error instanceof ApiError) return error.status >= 500;
  return false;
}

/**
 * ORGANISASJONEN BLE FRYST MENS MAN STO I DEN (0480). Hver forespørsel svarer nå
 * `ORGANIZATION_FROZEN`, og ingen side kan gjøre noe med det. Det aktive huset glemmes og appen
 * lastes på nytt: `/v1/me` velger et annet hus, eller `requireMembership` sender til fryst-skjermen.
 * Én gang, uansett hvor mange spørringer som feiler samtidig.
 */
let leavingFrozen = false;
function leaveFrozenOrganization(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.code !== 'ORGANIZATION_FROZEN') return false;
  if (leavingFrozen || typeof window === 'undefined') return true;
  leavingFrozen = true;
  setActiveOrganizationId(null);
  window.location.assign('/');
  return true;
}

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        staleTime: 30_000,
      },
      mutations: {
        retry: false,
      },
    },
    queryCache: new QueryCache({ onError: (error) => void leaveFrozenOrganization(error) }),
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _context, mutation) => {
        /*
         * EN HENTING SOM ALT GÅR, AVBRYTES FØRST (QA16, k1 31.10). En spørring uten data ennå (første
         * lasting) blir ikke hentet på nytt av `invalidateQueries`: TanStack gjenbruker hentingen som går,
         * og den startet før mutasjonen var lagret. Innboks sto tom med kontaktforespørselen som ble
         * laget i samme øyeblikk. `cancelQueries` først, så henter invalideringen fra ny.
         */
        for (const key of mutation.meta?.invalidates ?? []) {
          void client
            .cancelQueries({ queryKey: key })
            .then(() => client.invalidateQueries({ queryKey: key }));
        }
      },
      /*
       * EN MUTASJON SOM FEILER SKAL SI DET. ALLTID (QA13 N2, 20. september).
       *
       * Til i dag sto det bare `onSuccess` her. Hver eneste mutasjon uten sin egen `onError`
       * feilet derfor i stillhet: knappen sluttet å spinne, ingenting endret seg, ingen fikk vite
       * hvorfor. QA fant det på «Bruk standardforslag» — ruta svarte 503 og skjermen sa ingenting
       * — men knappen var bare den som ble trykket. Hullet var i laget under, og det gjaldt alle.
       *
       * Toast og ikke en kastet feil: en mislykket mutasjon er ikke en ødelagt side. Sida står, og
       * det man forsøkte skjedde ikke. `errorTitleKey` skiller allerede et AVSLAG (403 — «det får
       * du ikke») fra en FEIL («noe gikk galt»), og den forskjellen bæres videre hit: et avslag er
       * en advarsel, ikke en rød alarm.
       */
      onError: (error, _variables, _context, mutation) => {
        if (leaveFrozenOrganization(error)) return;
        if (mutation.meta?.errorHandledLocally === true) return;
        /* Mutasjonen har sin egen `onError` i `useMutation` — da er den presise, og den vinner. */
        if (mutation.options.onError !== undefined) return;

        /*
         * OG DEN TREDJE FORMEN: `mutate(verdi, { onError })`, valgt der man trykker.
         *
         * Den kan ikke leses herfra — den bor på observatøren, ikke på mutasjonen — så den måles
         * på virkningen i stedet: kom det en toast? React Query kaller cachen først og
         * observatøren etterpå, gjennom en mikrooppgave, så `setTimeout(0)` lander garantert bak
         * begge. Forsinkelsen er usynlig for den som leser, og alternativet er to bokser om det
         * samme trykket.
         */
        const before = toastsShown();
        setTimeout(() => {
          if (toastsShown() !== before) return;
          const refusal = error instanceof ApiError && error.status === 403;
          emitToast({
            tone: refusal ? 'warning' : 'error',
            title: i18n.t(errorTitleKey(error)),
            description: i18n.t(errorKey(error)),
          });
        }, 0);
      },
    }),
  });
  return client;
}
